import React from 'react';

import { useTranslation } from '@lyearn/i18n';
import { Stack, Typography } from '@lyearn/ui';
import { Dialog, Modal } from '@/components/Modals';
import { ModalVariant } from '@/components/Modals/types';
import DueDate from '@/modules/corporate/modules/assignment/views/AssignmentCreator/components/DueDate';
import { AssignmentItemTypeEnum } from '@/types/schema';

import { RenderRemoveDueDate } from '../../renderer';
import CollectionContents from '../CollectionContents';
import NestedPathModal from '../NestedPathModal';
import useSetDueDate from './hooks/useSetDueDate';
import { SetDueDateDialogProps } from './types';

export default function SetDueDateDialog(props: SetDueDateDialogProps) {
  const { t } = useTranslation();
  const {
    onCloseDueDateDialog,
    onSetDueDateForContent,
    onSetDueDateForSection,
    inViewItemForDueDate,
    nestedDialogState,
  } = props;

  const { state, method } = useSetDueDate(props);
  const { dialogState, selectedDueDate, dueDateError } = state;
  const { setSelectedDueDate, onSetDueDate, onReset, onSetNestedDueDate, onCloseNestedModal } =
    method;
  const { itemType, dueDateNestingLevel, editingContent, editingCollection } = inViewItemForDueDate;

  if (!dialogState) {
    return null;
  }

  const { title, description, Content, actionPrimaryText } = dialogState;

  if (dueDateNestingLevel) {
    if (itemType === AssignmentItemTypeEnum.Content && editingContent) {
      return (
        <NestedPathModal
          actionPrimaryText={actionPrimaryText}
          content={editingContent}
          description={description}
          dueDateNestingLevel={dueDateNestingLevel}
          nestedDueDateConfig={nestedDialogState}
          title={title}
          onCloseDueDateDialog={onCloseNestedModal}
          onReset={onReset}
          onSetDueDateForContent={onSetDueDateForContent}
          onSetDueDateForSection={onSetDueDateForSection}
          onSetNestedDueDate={onSetNestedDueDate}
        />
      );
    }
    if (itemType === AssignmentItemTypeEnum.Collection && editingCollection) {
      return (
        <CollectionContents
          actionPrimaryText={actionPrimaryText}
          collection={editingCollection}
          description={description}
          nestedDueDateConfig={nestedDialogState}
          title={title}
          onCloseDueDateDialog={onCloseNestedModal}
          onReset={onReset}
          onSetDueDateForContent={onSetDueDateForContent}
          onSetNestedDueDate={onSetNestedDueDate}
        />
      );
    }
  }

  return (
    <Modal
      action={{
        text: actionPrimaryText,
        onSubmit: onSetDueDate,
        TertiaryAction: RenderRemoveDueDate(t, onReset),
      }}
      header={{
        title,
        description,
      }}
      variant={ModalVariant.FullHeight}
      onClose={onCloseDueDateDialog}>
      <Dialog.Content className="flex flex-row">{Content}</Dialog.Content>
      <Stack className="mt-1 w-full px-32 pt-4" direction="col">
        <DueDate value={selectedDueDate} onChange={setSelectedDueDate} />
        {dueDateError ? (
          <Typography className="mt-8 ps-32" color="text-text-error" variant="body-short-03">
            {dueDateError}
          </Typography>
        ) : null}
      </Stack>
    </Modal>
  );
}
